import { OnSuccessPromise } from 'utils/applePay';
import {
  UseIsSupportedApplePayEnvironmentParams,
  useIsSupportedApplePayEnvironment,
} from 'hooks/apple-pay/useIsSupportedApplePayEnvironment';
import { core } from '@tock/coretypes';
import { useApplePayForBraintree } from 'hooks/apple-pay/useApplePayForBraintree';
import { useApplePayForStripe } from 'hooks/apple-pay/useApplePayForStripe';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import { useState } from 'react';

export type ApplePayProcessorType = 'BRAINTREE' | 'STRIPE';

export interface UseApplePayParams extends UseIsSupportedApplePayEnvironmentParams {
  amountCents?: number;
  applePayProcessorType?: ApplePayProcessorType;
  guest?: core.Patron;
  countryCode?: string;
  currencyCode?: string;
}

export interface UseApplePayResults {
  isApplePaySupported: boolean;
  isSupportedApplePayEnvironment: boolean;
  isApplePayLoading: boolean;
  hasCardInAppleWallet?: boolean;
  startApplePaySession: (onSuccess: OnSuccessPromise) => Promise<unknown>;
}

export interface UseProcessorApplePayParams extends UseApplePayParams {
  isSupportedTockEnvironment: boolean;
  setFailed: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSupportedDevice: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  setHasCardInAppleWallet: React.Dispatch<React.SetStateAction<boolean | undefined>>;
}

export interface UseProcessorApplePayResults {
  isEnabled: boolean;
  isLoading: boolean;
  startSession: (onSuccess: OnSuccessPromise) => Promise<unknown>;
}

export const useApplePay = (params: UseApplePayParams): UseApplePayResults => {
  const [isSupportedDevice, setIsSupportedDevice] = useState<boolean>();
  const [hasCardInAppleWallet, setHasCardInAppleWallet] = useState<boolean>();
  const [failed, setFailed] = useState<boolean>(false);

  const isFeatureEnabled = useFeatureFlag('ENABLE_APPLE_PAY');
  const isSupportedTockEnvironment = useIsSupportedApplePayEnvironment(params);

  const applePayHook =
    params.applePayProcessorType === 'STRIPE' ? useApplePayForStripe : useApplePayForBraintree;

  const { isEnabled, isLoading, startSession } = applePayHook({
    ...params,
    isSupportedTockEnvironment: isFeatureEnabled && isSupportedTockEnvironment,
    setFailed: setFailed,
    setIsSupportedDevice: setIsSupportedDevice,
    setHasCardInAppleWallet: setHasCardInAppleWallet,
  });

  if (!isEnabled || failed || isSupportedDevice === false) {
    return {
      isApplePayLoading: false,
      isApplePaySupported: false,
      hasCardInAppleWallet,
      startApplePaySession: startSession,
      isSupportedApplePayEnvironment: isSupportedTockEnvironment,
    };
  } else if (isSupportedDevice === undefined || hasCardInAppleWallet === undefined) {
    return {
      isApplePayLoading: true,
      isApplePaySupported: false,
      hasCardInAppleWallet,
      startApplePaySession: startSession,
      isSupportedApplePayEnvironment: isSupportedTockEnvironment,
    };
  } else {
    return {
      isApplePayLoading: isLoading,
      isApplePaySupported: true,
      hasCardInAppleWallet,
      startApplePaySession: startSession,
      isSupportedApplePayEnvironment: isSupportedTockEnvironment,
    };
  }
};
